
import {
  defineComponent, PropType, reactive, watch,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { EStatusProcessamento } from '@/models/Enumeradores/EStatusProcessamento';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import PreviewPdf from '@/core/components/Modal/PreviewPdf.vue';
import { ETipoArquivo } from '@/core/models/Enumeradores/ETipoArquivo';
import { IArquivoPdf } from '@/core/models/IArquivoPdf';
import SelecionarData from '@/core/components/Tela/SelecionarData.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import CteAbas from '../../../components/Compras/ConhecimentoTransporte/CteAbas.vue';
import SelecionarPessoa from '@/components/Cadastros/Pessoas/SelecionarPessoa.vue';
import SelecionarTipoOperacao from '@/components/Cadastros/SelecionarTipoOperacao.vue';
import SelecionarCfop from '@/components/Cadastros/Tributacoes/Cfops/SelecionarCfop.vue';
import { ETipoMovimentoComercial } from '@/core/models/Enumeradores/ETipoMovimentoComercial';
import ServicoEmpresa from '@/servicos/MeuSistema/ServicoEmpresa';
import { ICteImposto } from '@/models/Entidades/Compras/ConhecimentoTransporte/ICteImposto';
import { ICte } from '@/models/Entidades/Compras/ConhecimentoTransporte/ICte';
import { ETipoRelacaoPessoa } from '@/models/Enumeradores/Cadastros/Pessoas/ETipoRelacaoPessoa';
import servicoConhecimentoTransporte from '@/servicos/Fiscal/ServicoConhecimentoTransporte';
import { ETipoParticipante } from '@/models/Enumeradores/Fiscal/ETipoParticipante';
import ServicoPessoa from '@/servicos/Cadastros/Pessoas/ServicoPessoa';
import { IPessoa } from '@/models/Entidades/Cadastros/Pessoas/IPessoa';

function criarImpostoInicial(): ICteImposto {
  return {
    valorTotalTributosAproximado: 0,
    icms: {
      cstIcms: '',
      baseCalculo: 0,
      aliquota: 0,
      valorIcms: 0,
      valorBaseCalculoSt: 0,
      valorIcmsSt: 0,
      percentualReducaoBaseCalculo: 0,
    },
    iss: {
      baseCalculo: 0,
      aliquota: 0,
      valorIss: 0,
      codigoMunicipioFatoGerador: 0,
    },
    pis: {
      cstPis: '',
      baseCalculo: 0,
      aliquota: 0,
      valorPis: 0,
    },
    cofins: {
      cstCofins: '',
      baseCalculo: 0,
      aliquota: 0,
      valorCofins: 0,
    },
  };
}

function criarConhecimentoTransporteInicial(): ICte {
  return {
    codigo: 0,
    codigoEmpresa: 0,
    chaveAcesso: '',
    numeroCte: 0,
    cfopEntrada: 0,
    dataEmissao: '',
    codigoTransportadora: 0,
    codigoPessoaTransportadora: 0,
    codigoTomador: 0,
    valorTotalPrestacao: 0,
    valorTotalImposto: 0,
    imposto: criarImpostoInicial(),
    detalhesPagamento: [],
    documentosReferenciados: [],
    participantes: [],
    codigoTipoOperacao: 0,
    statusProcessamento: EStatusProcessamento.EmDigitacao,
    status: 1,
    origem: 0,
    ordem: 0,
    xmlIntegral: '',
  };
}

function normalizarImposto(imposto?: Partial<ICteImposto> | null): ICteImposto {
  const impostoInicial = criarImpostoInicial();

  return {
    ...impostoInicial,
    ...(imposto ?? {}),
    icms: {
      ...impostoInicial.icms,
      ...(imposto?.icms ?? {}),
    },
    iss: {
      ...impostoInicial.iss,
      ...(imposto?.iss ?? {}),
    },
    pis: {
      ...impostoInicial.pis,
      ...(imposto?.pis ?? {}),
    },
    cofins: {
      ...impostoInicial.cofins,
      ...(imposto?.cofins ?? {}),
    },
  };
}

function normalizarDocumentoReferenciado(documento: any) {
  return {
    ...documento,
    codigo: Number(documento?.codigo ?? 0),
    codigoCte: Number(documento?.codigoCte ?? documento?.codigoConhecimento ?? 0),
    tipoDocumento: documento?.tipoDocumento ?? undefined,
    numeroDocumento: Number(documento?.numeroDocumento ?? 0) || undefined,
    serie: documento?.serie ?? undefined,
    dataEmissao: documento?.dataEmissao ?? null,
    codigoNotaFiscalEntrada: Number(documento?.codigoNotaFiscalEntrada ?? 0),
    chaveAcessoDocumento: (
      documento?.chaveAcesso
      ?? documento?.chaveAcessoDocumento
      ?? documento?.ChaveAcessoDocumento
      ?? documento?.chaveAcessoDoc
      ?? ''
    ).toString(),
    valorDocumento: Number(documento?.valorDocumento ?? 0),
    valorFretePorDocumento: Number(documento?.valorFretePorDocumento ?? 0),
    pesoDocumento: Number(documento?.pesoDocumento ?? 0),
  };
}

function normalizarDetalhePagamento(detalhe: any) {
  return {
    ...detalhe,
    codigo: Number(detalhe?.codigo ?? 0),
    codigoCte: Number(detalhe?.codigoCte ?? 0),
    codigoTipoDocumentoFinanceiro: Number(detalhe?.codigoTipoDocumentoFinanceiro ?? 0),
    codigoConta: Number(detalhe?.codigoConta ?? 0),
    codigoPlanoContaCategoria: Number(detalhe?.codigoPlanoContaCategoria ?? 0),
    codigoTituloFinanceiro: Number(detalhe?.codigoTituloFinanceiro ?? 0),
    parcela: Number(detalhe?.parcela ?? 0),
    valorPagamento: Number(detalhe?.valorPagamento ?? 0),
    acrescimos: Number(detalhe?.acrescimos ?? 0),
    outrasTaxas: Number(detalhe?.outrasTaxas ?? 0),
    valorTotal: Number(detalhe?.valorTotal ?? 0),
    anexos: detalhe?.anexos ?? [],
    centrosCustos: detalhe?.centrosCustos ?? [],
  };
}

function converterDataEmissao(valor?: string | Date): string {
  if (!valor) {
    return '';
  }

  if (typeof valor === 'string') {
    return valor;
  }

  return valor.toISOString();
}

function normalizarConhecimentoTransporte(conhecimentoTransporte?: Partial<ICte>): ICte {
  const conhecimentoTransporteInicial = criarConhecimentoTransporteInicial();
  if (!conhecimentoTransporte) {
    return conhecimentoTransporteInicial;
  }

  const numeroCte = Number((conhecimentoTransporte as any).numeroCte ?? (conhecimentoTransporte as any).numeroConhecimento ?? 0);
  const valorTotalPrestacao = Number((conhecimentoTransporte as any).valorTotalPrestacao ?? (conhecimentoTransporte as any).valor ?? 0);

  return {
    ...conhecimentoTransporteInicial,
    ...conhecimentoTransporte,
    numeroCte,
    valorTotalPrestacao,
    dataEmissao: converterDataEmissao(conhecimentoTransporte.dataEmissao),
    imposto: normalizarImposto(conhecimentoTransporte.imposto),
    detalhesPagamento: (conhecimentoTransporte.detalhesPagamento ?? []).map(normalizarDetalhePagamento),
    documentosReferenciados: (conhecimentoTransporte.documentosReferenciados ?? []).map(normalizarDocumentoReferenciado),
    participantes: conhecimentoTransporte.participantes ?? [],
    statusProcessamento: conhecimentoTransporte.statusProcessamento ?? EStatusProcessamento.EmDigitacao,
    xmlIntegral: conhecimentoTransporte.xmlIntegral ?? '',
    codigoPessoaTransportadora: Number(conhecimentoTransporte.codigoPessoaTransportadora ?? 0),
    status: Number((conhecimentoTransporte as any).status ?? 1),
    origem: Number((conhecimentoTransporte as any).origem ?? 0),
    ordem: Number((conhecimentoTransporte as any).ordem ?? 0),
  };
}

function obterCpfCnpjPessoa(pessoa?: IPessoa): string {
  return pessoa?.pessoaJuridica?.cnpj ?? pessoa?.pessoaFisica?.cpf ?? '';
}

function obterNomeRazaoSocialPessoa(pessoa?: IPessoa): string {
  return pessoa?.pessoaJuridica?.razaoSocial ?? pessoa?.pessoaFisica?.nomeCompleto ?? '';
}

function obterInscricaoEstadualPessoa(pessoa?: IPessoa): string {
  return pessoa?.inscricoes?.find((inscricao) => inscricao.principal)?.inscricaoEstadual
    ?? pessoa?.inscricoes?.[0]?.inscricaoEstadual
    ?? '';
}

function obterNomeCidadePessoa(pessoa?: IPessoa): string {
  return pessoa?.enderecos?.[0]?.endereco?.cidade?.nome ?? '';
}

function obterUfPessoa(pessoa?: IPessoa): string {
  const cidade: any = pessoa?.enderecos?.[0]?.endereco?.cidade;
  return cidade?.estado?.sigla ?? cidade?.uf ?? cidade?.siglaUf ?? '';
}

async function criarPayloadSalvar(conhecimentoTransporte: ICte, servicoPessoa: ServicoPessoa, empresa: number): Promise<ICte> {
  const documentosReferenciadosPayload = (conhecimentoTransporte.documentosReferenciados ?? []).map((documento) => {
    const chaveAcessoDocumento = (
      documento.chaveAcessoDocumento
      ?? (documento as any).chaveAcesso
      ?? (documento as any).chaveAcessoDoc
      ?? ''
    ).toString();
    const documentoSemChave = { ...(documento as any) };
    delete documentoSemChave.chaveAcessoDocumento;
    delete documentoSemChave.chaveAcesso;
    delete documentoSemChave.chaveAcessoDoc;
    return {
      ...documentoSemChave,
      ChaveAcessoDocumento: chaveAcessoDocumento,
    };
  });

  const participantes = conhecimentoTransporte.participantes ?? [];
  const participanteTomadorExistente = participantes.find(
    (participante) => participante.tipoParticipante === ETipoParticipante.Tomador,
  );
  const codigoTomador = Number(conhecimentoTransporte.codigoTomador) || 0;
  let pessoaTomador: IPessoa | undefined;
  let ufTomador = participanteTomadorExistente?.uf ?? '';

  if (codigoTomador > 0) {
    try {
      pessoaTomador = await servicoPessoa.obter(codigoTomador, empresa);
      if (!ufTomador) {
        ufTomador = obterUfPessoa(pessoaTomador);
      }
      if (!ufTomador) {
        ufTomador = await servicoPessoa.obterSiglaUf(codigoTomador);
      }
    } catch {
      pessoaTomador = undefined;
    }
  }

  const participanteTomador = {
    codigo: participanteTomadorExistente?.codigo ?? 0,
    codigoCte: Number(conhecimentoTransporte.codigo ?? 0),
    codigoPessoa: codigoTomador || null,
    tipoParticipante: ETipoParticipante.Tomador,
    cpfCnpj: obterCpfCnpjPessoa(pessoaTomador) || participanteTomadorExistente?.cpfCnpj || '',
    nomeRazaoSocial: obterNomeRazaoSocialPessoa(pessoaTomador) || participanteTomadorExistente?.nomeRazaoSocial || '',
    inscricaoEstadual: obterInscricaoEstadualPessoa(pessoaTomador) || participanteTomadorExistente?.inscricaoEstadual || '',
    uf: ufTomador,
    nomeCidade: obterNomeCidadePessoa(pessoaTomador) || participanteTomadorExistente?.nomeCidade || '',
  };

  return {
    ...criarConhecimentoTransporteInicial(),
    ...conhecimentoTransporte,
    codigo: Number(conhecimentoTransporte.codigo ?? 0),
    codigoEmpresa: Number(conhecimentoTransporte.codigoEmpresa ?? 0),
    numeroCte: Number((conhecimentoTransporte as any).numeroCte ?? (conhecimentoTransporte as any).numeroConhecimento ?? 0),
    cfopEntrada: Number(conhecimentoTransporte.cfopEntrada ?? 0),
    dataEmissao: converterDataEmissao(conhecimentoTransporte.dataEmissao),
    statusProcessamento: conhecimentoTransporte.statusProcessamento ?? EStatusProcessamento.EmDigitacao,
    codigoTransportadora: Number(conhecimentoTransporte.codigoTransportadora ?? 0),
    codigoPessoaTransportadora: Number(conhecimentoTransporte.codigoPessoaTransportadora ?? 0),
    codigoTomador: Number(conhecimentoTransporte.codigoTomador ?? 0),
    codigoTipoOperacao: Number(conhecimentoTransporte.codigoTipoOperacao ?? 0),
    xmlIntegral: conhecimentoTransporte.xmlIntegral ?? '',
    valorTotalPrestacao: Number((conhecimentoTransporte as any).valorTotalPrestacao ?? (conhecimentoTransporte as any).valor ?? 0),
    valorTotalImposto: Number((conhecimentoTransporte as any).valorTotalImposto ?? 0),
    status: Number((conhecimentoTransporte as any).status ?? 1),
    origem: Number((conhecimentoTransporte as any).origem ?? 0),
    ordem: Number((conhecimentoTransporte as any).ordem ?? 0),
    imposto: normalizarImposto(conhecimentoTransporte.imposto),
    documentosReferenciados: documentosReferenciadosPayload as any,
    participantes: [
      ...participantes.filter((participante) => participante.tipoParticipante !== ETipoParticipante.Tomador),
      participanteTomador,
    ],
    detalhesPagamento: (conhecimentoTransporte.detalhesPagamento ?? []).map((detalhe) => ({
      ...normalizarDetalhePagamento(detalhe),
      codigoPessoa: Number(conhecimentoTransporte.codigoPessoaTransportadora ?? 0),
    })),
  };
}

export default defineComponent({
  name: 'ConhecimentoTransporteModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
    conhecimentoTransporte: {
      type: Object as PropType<Partial<ICte>>,
    },
    empresa: {
      type: Number,
      required: true,
    },
  },
  components: {
    Icone,
    RequisicaoModal,
    SelecionarData,
    PreviewPdf,
    CampoNumerico,
    CteAbas,
    SelecionarPessoa,
    SelecionarTipoOperacao,
    SelecionarCfop,
  },
  emits: ['update:operacao', 'update:visivel', 'confirmacao'],
  setup(props, { emit }) {
    const {
      telaBase,
      apresentarMensagemErro,
      apresentarMensagemSucesso,
    } = useTelaBase();
    const {
      modalBase,
      apresentarRetornoRequisicao,
      apresentarBarraProgresso,
      ocultarBarraProgresso,
    } = useModalBase(props, emit);
    const servicoEmpresa = new ServicoEmpresa();
    servicoEmpresa.requisicaoSistema();
    const servicoPessoa = new ServicoPessoa();
    servicoPessoa.requisicaoSistema();

    const state = reactive({
      conhecimentoTransporte: criarConhecimentoTransporteInicial(),
      arquivosPdf: [] as IArquivoPdf[],
    });

    function obterCodigoEmpresaSelecionada(): number {
      return Number(props.operacao.empresaSelecionada || props.empresa || 0);
    }

    async function preencherTomadorPadraoPorEmpresa() {
      if (state.conhecimentoTransporte.codigoTomador > 0 || props.operacao.codigoRegistro > 0) {
        return;
      }

      try {
        const dadosEmpresa = await servicoEmpresa.obter(props.empresa);
        const codigoPessoaEmpresa = Number((dadosEmpresa as any).codigoPessoa ?? 0);
        if (codigoPessoaEmpresa > 0) {
          state.conhecimentoTransporte.codigoTomador = codigoPessoaEmpresa;
        }
      } catch {
        state.conhecimentoTransporte.codigoTomador = state.conhecimentoTransporte.codigoTomador || 0;
      }
    }

    async function objetoInicial() {
      state.conhecimentoTransporte = normalizarConhecimentoTransporte(props.conhecimentoTransporte);
      state.conhecimentoTransporte.codigoEmpresa = obterCodigoEmpresaSelecionada();
      await preencherTomadorPadraoPorEmpresa();
    }

    async function AtualizarTela() {
      telaBase.carregando = true;
      await objetoInicial();
      if (modalBase.computedVisivel && props.operacao.codigoRegistro > 0) {
        try {
          const conhecimento = await servicoConhecimentoTransporte.obterPorCodigo(props.operacao.codigoRegistro, obterCodigoEmpresaSelecionada());
          state.conhecimentoTransporte = normalizarConhecimentoTransporte(conhecimento);
          if (!state.conhecimentoTransporte.codigoEmpresa || state.conhecimentoTransporte.codigoEmpresa <= 0) {
            state.conhecimentoTransporte.codigoEmpresa = obterCodigoEmpresaSelecionada();
          }
        } catch (error) {
          apresentarMensagemErro('Não foi possível carregar o CT-e para edição.');
        }
      }
      telaBase.carregando = false;
    }

    function validarSalvamento(): boolean {
      if (!state.conhecimentoTransporte.chaveAcesso) {
        apresentarMensagemErro('Informe a chave de acesso.');
        return false;
      }
      if (!state.conhecimentoTransporte.numeroCte || state.conhecimentoTransporte.numeroCte <= 0) {
        apresentarMensagemErro('Informe o número do conhecimento.');
        return false;
      }
      if (!state.conhecimentoTransporte.dataEmissao) {
        apresentarMensagemErro('Informe a data de emissão.');
        return false;
      }
      if (!state.conhecimentoTransporte.codigoTransportadora || state.conhecimentoTransporte.codigoTransportadora <= 0) {
        apresentarMensagemErro('Informe a transportadora.');
        return false;
      }
      if (!state.conhecimentoTransporte.codigoTomador || state.conhecimentoTransporte.codigoTomador <= 0) {
        apresentarMensagemErro('Informe o tomador.');
        return false;
      }
      if (!state.conhecimentoTransporte.codigoTipoOperacao || state.conhecimentoTransporte.codigoTipoOperacao <= 0) {
        apresentarMensagemErro('Informe o tipo de operação.');
        return false;
      }
      if (!state.conhecimentoTransporte.cfopEntrada || state.conhecimentoTransporte.cfopEntrada <= 0) {
        apresentarMensagemErro('Informe o CFOP de entrada.');
        return false;
      }
      return true;
    }

    async function salvar() {
      if (!validarSalvamento()) {
        return;
      }

      if (!state.conhecimentoTransporte.codigoEmpresa || state.conhecimentoTransporte.codigoEmpresa <= 0) {
        state.conhecimentoTransporte.codigoEmpresa = obterCodigoEmpresaSelecionada();
      }

      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };
      const payload = await criarPayloadSalvar(
        state.conhecimentoTransporte,
        servicoPessoa,
        obterCodigoEmpresaSelecionada(),
      );

      apresentarBarraProgresso('Aguarde por favor... Estamos salvando o CT-e');
      try {
        if (payload.codigo > 0) {
          retorno = await servicoConhecimentoTransporte.alterar(payload.codigo, payload);
        } else {
          retorno = await servicoConhecimentoTransporte.incluir(payload);
        }
      } catch (error) {
        ocultarBarraProgresso();
        apresentarMensagemErro('Erro ao salvar CT-e.');
        return;
      }
      ocultarBarraProgresso();

      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        apresentarMensagemSucesso(retorno.mensagem || 'CT-e salvo com sucesso.');
        emit('confirmacao');
        modalBase.computedVisivel = false;
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    watch(() => props.visivel, async (visivel) => {
      if (visivel) {
        await AtualizarTela();
      }
    }, { immediate: true });

    async function imprimir() {
      const retornoRelatorio = await servicoConhecimentoTransporte.gerarImpressao(
        props.operacao.codigoRegistro,
        obterCodigoEmpresaSelecionada(),
        ETipoArquivo.PDF,
      );
      if (retornoRelatorio.status === EStatusRetornoRequisicao.Sucesso) {
        state.arquivosPdf = [{ nome: '', link: retornoRelatorio.link } as IArquivoPdf];
      } else {
        apresentarMensagemErro(retornoRelatorio.mensagem);
      }
    }

    return {
      telaBase,
      props,
      modalBase,
      state,
      salvar,
      imprimir,
      objetoInicial,
      EPermissaoDados,
      ETipoMovimentoComercial,
      ETipoRelacaoPessoa,
    };
  },
});
